import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export function KeyboardShortcutsReference() {
  const shortcuts = [
    {
      category: 'Global',
      icon: '🌐',
      shortcuts: [
        { keys: ['Ctrl', 'K'], description: 'Open command palette', platform: 'all' },
        { keys: ['Cmd', 'K'], description: 'Open command palette', platform: 'mac' },
        { keys: ['?'], description: 'Show keyboard shortcuts', platform: 'all' },
        { keys: ['Escape'], description: 'Close dialogs and menus', platform: 'all' },
      ],
    },
    {
      category: 'Canvas',
      icon: '🎨',
      shortcuts: [
        { keys: ['Right Click'], description: 'Open node creation menu', platform: 'all' },
        { keys: ['Delete'], description: 'Delete selected node', platform: 'all' },
        { keys: ['Escape'], description: 'Deselect current node', platform: 'all' },
        { keys: ['+'], description: 'Zoom in', platform: 'all' },
        { keys: ['-'], description: 'Zoom out', platform: 'all' },
        { keys: ['Click'], description: 'Select node for configuration', platform: 'all' },
      ],
    },
    {
      category: 'Workflow',
      icon: '⚙️',
      shortcuts: [
        { keys: ['Ctrl', 'S'], description: 'Save workflow (or click Save button)', platform: 'all' },
        { keys: ['Cmd', 'S'], description: 'Save workflow (or click Save button)', platform: 'mac' },
        { keys: ['Ctrl', 'Enter'], description: 'Run workflow (or click Run button)', platform: 'all' },
        { keys: ['Cmd', 'Enter'], description: 'Run workflow (or click Run button)', platform: 'mac' },
      ],
    },
    {
      category: 'Node Configuration',
      icon: '⚡',
      shortcuts: [
        { keys: ['Tab'], description: 'Move to next input field', platform: 'all' },
        { keys: ['Shift', 'Tab'], description: 'Move to previous input field', platform: 'all' },
        { keys: ['Enter'], description: 'Confirm input or submit form', platform: 'all' },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Keyboard Shortcuts</h2>
        <p className="text-muted-foreground">
          Master the workflow builder with these keyboard shortcuts. Mix and match for faster workflow creation.
        </p>
      </div>

      <div className="grid gap-6">
        {shortcuts.map((group) => (
          <Card key={group.category} className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{group.icon}</span>
              <h3 className="text-lg font-semibold text-foreground">{group.category}</h3>
            </div>

            <div className="space-y-3">
              {group.shortcuts.map((shortcut, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded border border-border hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">{shortcut.description}</p>
                    {shortcut.platform !== 'all' && (
                      <Badge variant="outline" className="mt-1 text-xs">
                        {shortcut.platform === 'mac' ? 'macOS only' : 'Windows/Linux'}
                      </Badge>
                    )}
                  </div>

                  <div className="flex gap-1 flex-wrap justify-end">
                    {shortcut.keys.map((key, i) => (
                      <div key={i} className="flex items-center gap-1">
                        <kbd className="px-2.5 py-1.5 text-xs font-semibold text-foreground bg-muted border border-border rounded shadow-sm">
                          {key}
                        </kbd>
                        {i < shortcut.keys.length - 1 && (
                          <span className="text-xs text-muted-foreground px-1">+</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-6 bg-blue-500/5 border-blue-500/20 space-y-4">
        <h3 className="font-semibold text-foreground">Tips</h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• Use Ctrl+K (or Cmd+K on Mac) to quickly navigate and execute commands</li>
          <li>• Press Delete to remove selected nodes without confirmation</li>
          <li>• Right-click frequently to access the context menu for adding nodes</li>
          <li>• Use Tab to navigate between fields in node configuration panels</li>
          <li>• Press Escape to deselect the current node and close dialogs</li>
        </ul>
      </Card>

      <Card className="p-6 bg-amber-500/5 border-amber-500/20 space-y-4">
        <h3 className="font-semibold text-foreground">Coming Soon</h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• Ctrl+Z / Cmd+Z - Undo last action</li>
          <li>• Ctrl+Y / Cmd+Y - Redo last action</li>
          <li>• Ctrl+C / Cmd+C - Copy selected nodes</li>
          <li>• Ctrl+V / Cmd+V - Paste copied nodes</li>
          <li>• Ctrl+D / Cmd+D - Duplicate selected node</li>
        </ul>
      </Card>
    </div>
  );
}
